import React, { useState, useEffect } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import {
  formatBookingDates,
  getFirstBookingDates,
  isArrayLength,
} from '../../../util/genericHelpers';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  isSameDay,
  isInRange,
  monthIdString,
} from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';

import {
  Form,
  PrimaryButton,
  H6,

} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './BookingDatesForm.module.css';
import moment from 'moment';
import CustomRangeInput from '../CustomRangeInput/CustomRangeInput';

const TODAY = new Date();

/**
 * Return a boolean indicating if given date can be found in an array
 * of tile slots (start dates).
 */
const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

/**
 * Find first blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Object} monthlyTimeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

/**
 * Find last blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Array} timeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  if (isSameDay(previousDate, startDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

// In case start or end date for the booking is missing
// focus on that input, otherwise continue with the
// default handleSubmit function.
const handleFormSubmit = (setFocusedInput, onSubmit, bookingSlots) => e => {
  const { startDate, endDate } = getFirstBookingDates(bookingSlots) || {};
  if (!startDate) {
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    setFocusedInput(END_DATE);
  } else {
    onSubmit(e);
  }
};

// Function that can be passed to nested components
// so that they can notify this component when the
// focused input changes.

// When the values of the form are updated we need to fetch
// lineItems from this Template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFormSpyChange = (
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
) => formValues => {
  const { selectedDates } = formValues?.values;
  const bookingSlots = formatBookingDates(selectedDates);
  const { startDate, endDate } = getFirstBookingDates(bookingSlots) || {};

  if (startDate && endDate && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        availableSlots: bookingSlots,
      },
      listingId,
      isOwnListing,
    });
  }
};

export const BookingDatesFormComponent = props => {
  const [currentMonth, setCurrentMonth] = useState(getStartOf(TODAY, 'month', props.timeZone));
  const [focusedInput, setFocusedInput] = useState(START_DATE);
  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    if (props.onMonthChanged) {
      props.onMonthChanged(monthId);
    }
  }, [currentMonth]);

  const {
    rootClassName,
    className,
    price: unitPrice,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    dayCountAvailableForBooking,
    marketplaceName,
    payoutDetailsWarning,
    ...rest
  } = props;
  const bookingSlots = formatBookingDates(selectedDates);
  const classes = classNames(rootClassName || css.root, className);
  const onFormSubmit = handleFormSubmit(setFocusedInput, onSubmit, bookingSlots);

  const isDateBlocked = day => {
    if (day.isBefore(moment().startOf('day'))) {
      return true;
    }
  };

  return (
    <FinalForm
      {...rest}
      unitPrice={unitPrice}
      onSubmit={onFormSubmit}
      render={fieldRenderProps => {
        const {
          handleSubmit,
          lineItems,
          fetchLineItemsError,
          form,
          values,
          monthlyTimeSlots
        } = fieldRenderProps;

        const onFormSpyChange = handleFormSpyChange(
          listingId,
          isOwnListing,
          fetchLineItemsInProgress,
          onFetchTransactionLineItems,
          monthlyTimeSlots,
          bookingSlots
        );

        const { startDate, endDate } = getFirstBookingDates(bookingSlots) || {};

        const updatedAvailableSlots =
          isArrayLength(bookingSlots) &&
          bookingSlots.map(({ startDate, endDate, displayEndDate, ...rest }) => rest);

        if (updatedAvailableSlots) {
          values.availableSlots = updatedAvailableSlots;
          values.startDate = startDate;
          values.endDate = endDate;
          values.bookingDates = { startDate, endDate };
        }

        const allTimeSlots = Object.values(monthlyTimeSlots).flatMap(
          month => month.timeSlots
        );

        const breakdownData =
          startDate && endDate
            ? {
                startDate,
                endDate,
              }
            : null;

        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

        return (
          <Form onSubmit={handleSubmit} className={classes}>
            <FormSpy subscription={{ values: true }} onChange={onFormSpyChange} />

            <CustomRangeInput
              isOutsideRange={isDateBlocked}
              setSelectedDates={setSelectedDates}
              selectedDates={selectedDates}
              form={form}
              focusedInput={focusedInput}
              setFocusedInput={setFocusedInput}
              className={css.customCalendar}
              allTimeSlots={allTimeSlots}
              isBookingDaysForm={true}
            />

            {selectedDates.length > 0 ? (
              <div className={css.availableSlotsSec}>
                <p className={css.slotHeadingText}>
                  <FormattedMessage
                    id="BookingDatesForm.availableEventsHeading"
                    values={{ slots: selectedDates.length }}
                  />
                </p>
                {selectedDates.map((date, index) => (
                  <div key={index} className={css.slotContainer}>
                    <div className={css.availableDateTime}>
                      <span>{moment(date).format('YYYY-MM-DD')}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                </H6>
                <hr className={css.totalDivider} />
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                />
              </div>
            ) : null}
            {fetchLineItemsError ? (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
              </span>
            ) : null}

            <div className={css.submitButton}>
              <PrimaryButton type="submit" inProgress={fetchLineItemsInProgress}>
                <FormattedMessage id="BookingDatesForm.requestToBook" />
              </PrimaryButton>
            </div>
            <p className={css.finePrint}>
              {payoutDetailsWarning ? (
                payoutDetailsWarning
              ) : (
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingDatesForm.ownListing'
                      : 'BookingDatesForm.youWontBeChargedInfo'
                  }
                />
              )}
            </p>
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
